import {updateURLsPrompt} from '../../prompts/dev.js'
import {AppConfigurationInterface, AppLinkedInterface, CurrentAppConfiguration} from '../../models/app/app.js'
import {UpdateURLsSchema, UpdateURLsVariables} from '../../api/graphql/update_urls.js'
import {setCachedAppInfo} from '../local-storage.js'
import {AppConfigurationUsedByCli} from '../../models/extensions/specifications/types/app_config.js'
import {prependApplicationUrl} from '../../models/extensions/specifications/validation/url_prepender.js'
import {DeveloperPlatformClient} from '../../utilities/developer-platform-client.js'
import {setManyAppConfigValues} from '../app/patch-app-configuration-file.js'
import {AbortError, BugError} from '@shopify/cli-kit/node/error'
import {Config} from '@oclif/core'
import {isValidURL} from '@shopify/cli-kit/common/url'
import {codespaceURL, codespacePortForwardingDomain, gitpodURL} from '@shopify/cli-kit/node/context/local'
import {fanoutHooks} from '@shopify/cli-kit/node/plugins'
import {terminalSupportsPrompting} from '@shopify/cli-kit/node/system'
import {TunnelClient} from '@shopify/cli-kit/node/plugins/tunnel'
import {outputDebug} from '@shopify/cli-kit/node/output'

interface AppProxy {
  proxyUrl: string
  proxySubPath: string
  proxySubPathPrefix: string
}

export interface ApplicationURLs {
  applicationUrl: string
  redirectUrlWhitelist: string[]
  appProxy?: AppProxy
}

export type FrontendURLOptions = UseLocalhostFrontendUrlOptions | UseTunnelFrontendUrlOptions

interface UseLocalhostFrontendUrlOptions {
  noTunnelUseLocalhost: true
  port: number
}

interface UseTunnelFrontendUrlOptions {
  noTunnelUseLocalhost: false
  tunnelUrl?: string
  tunnelClient: TunnelClient | undefined
}

interface FrontendURLResult {
  frontendUrl: string
  frontendPort: number
  usingLocalhost: boolean
}

/**
 * The tunnel creation logic depends on 7 variables:
 * - If a Codespaces environment is detected, then the URL is built using the codespaces hostname. No need for tunnel
 * - If a Gitpod environment is detected, then the URL is built using the gitpod hostname. No need for tunnel
 *   No need for tunnel. In case problems with that configuration, the flags Tunnel or Custom Tunnel url could be used
 * - If a tunnelUrl is provided, that takes preference and is returned as the frontendURL
 * - If noTunnel is true, that takes second preference and localhost is used
 * - Otherwise, a tunnel is created. (by default using cloudflare)
 *
 * If there is no cached tunnel plugin and a tunnel is necessary, we'll ask the user to confirm.
 */
export async function generateFrontendURL(options: FrontendURLOptions): Promise<FrontendURLResult> {
  if (options.noTunnelUseLocalhost) {
    return {
      frontendUrl: 'https://localhost',
      frontendPort: options.port,
      usingLocalhost: true,
    }
  }

  let frontendPort = 4040
  let frontendUrl = ''
  const usingLocalhost = options.noTunnelUseLocalhost

  if (codespaceURL()) {
    frontendUrl = `https://${codespaceURL()}-${frontendPort}.${codespacePortForwardingDomain()}`
    return {frontendUrl, frontendPort, usingLocalhost}
  }

  if (gitpodURL()) {
    const defaultUrl = gitpodURL()?.replace('https://', '')
    frontendUrl = `https://${frontendPort}-${defaultUrl}`
    return {frontendUrl, frontendPort, usingLocalhost}
  }

  if (options.tunnelUrl) {
    const matches = options.tunnelUrl.match(/(https:\/\/[^:]+):([0-9]+)/)
    if (!matches) {
      throw new AbortError(`Invalid tunnel URL: ${options.tunnelUrl}`, 'Valid format: "https://my-tunnel-url:port"')
    }
    frontendPort = Number(matches[2])
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    frontendUrl = matches[1]!
    return {frontendUrl, frontendPort, usingLocalhost}
  }

  if (options.tunnelClient) {
    frontendPort = options.tunnelClient.port
    frontendUrl = await pollTunnelURL(options.tunnelClient)
  }

  return {frontendUrl, frontendPort, usingLocalhost}
}

/**
 * Poll the tunnel provider every 0.5 until an URL or error is returned.
 */
async function pollTunnelURL(tunnelClient: TunnelClient): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    let retries = 0
    const pollTunnelStatus = async () => {
      const result = tunnelClient.getTunnelStatus()
      outputDebug(`Polling tunnel status for ${tunnelClient.provider} (attempt ${retries}): ${result.status}`)
      if (result.status === 'error') return reject(new AbortError(result.message, result.tryMessage))
      if (result.status === 'connected') {
        resolve(result.url)
      } else {
        retries += 1
        startPolling()
      }
    }
    const startPolling = () => {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      setTimeout(pollTunnelStatus, 500)
    }

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    pollTunnelStatus()
  })
}

export function generateApplicationURLs(
  baseURL: string,
  authCallbackPath?: string | string[],
  proxyFields?: CurrentAppConfiguration['app_proxy'],
): ApplicationURLs {
  let redirectUrlWhitelist: string[]
  if (authCallbackPath && authCallbackPath.length > 0) {
    const authCallbackPaths = Array.isArray(authCallbackPath) ? authCallbackPath : [authCallbackPath]
    redirectUrlWhitelist = authCallbackPaths.reduce<string[]>((acc, path) => {
      if (path && path.length > 0) {
        acc.push(`${baseURL}${path}`)
      }
      return acc
    }, [])
  } else {
    redirectUrlWhitelist = [
      `${baseURL}/auth/callback`,
      `${baseURL}/auth/shopify/callback`,
      `${baseURL}/api/auth/callback`,
    ]
  }

  const appProxy = proxyFields
    ? {
        appProxy: {
          proxyUrl: replaceHost(prependApplicationUrl(proxyFields.url, baseURL), baseURL),
          proxySubPath: proxyFields.subpath,
          proxySubPathPrefix: proxyFields.prefix,
        },
      }
    : {}

  return {
    applicationUrl: baseURL,
    redirectUrlWhitelist,
    ...appProxy,
  }
}

function replaceHost(oldUrl: string, newUrl: string): string {
  const oldUrlObject = new URL(oldUrl)
  const newUrlObject = new URL(newUrl)
  oldUrlObject.host = newUrlObject.host
  return oldUrlObject.toString().replace(/\/$/, '')
}

export async function updateURLs(
  urls: ApplicationURLs,
  apiKey: string,
  developerPlatformClient: DeveloperPlatformClient,
  localApp?: AppConfigurationInterface,
): Promise<void> {
  const variables: UpdateURLsVariables = {apiKey, ...urls}
  const result: UpdateURLsSchema = await developerPlatformClient.updateURLs(variables)
  if (result.appUpdate.userErrors.length > 0) {
    const errors = result.appUpdate.userErrors.map((error) => error.message).join(', ')
    throw new AbortError(errors)
  }

  if (localApp && localApp.configuration.client_id === apiKey) {
    const configValues = [
      {keyPath: 'application_url', value: urls.applicationUrl},
      {keyPath: 'auth.redirect_urls', value: urls.redirectUrlWhitelist},
    ]

    if (urls.appProxy) {
      configValues.push(
        {keyPath: 'app_proxy.url', value: urls.appProxy.proxyUrl},
        {keyPath: 'app_proxy.subpath', value: urls.appProxy.proxySubPath},
        {keyPath: 'app_proxy.prefix', value: urls.appProxy.proxySubPathPrefix},
      )
    }

    await setManyAppConfigValues(localApp.configuration.path, configValues)
  }
}

export async function getURLs(remoteAppConfig?: AppConfigurationUsedByCli): Promise<ApplicationURLs> {
  const result: ApplicationURLs = {
    applicationUrl: remoteAppConfig?.application_url ?? '',
    redirectUrlWhitelist: remoteAppConfig?.auth?.redirect_urls ?? [],
  }
  if (remoteAppConfig?.app_proxy) {
    result.appProxy = {
      proxyUrl: remoteAppConfig?.app_proxy.url,
      proxySubPath: remoteAppConfig?.app_proxy.subpath,
      proxySubPathPrefix: remoteAppConfig?.app_proxy.prefix,
    }
  }
  return result
}

interface ShouldOrPromptUpdateURLsOptions {
  currentURLs: ApplicationURLs
  appDirectory: string
  cachedUpdateURLs?: boolean
  newApp?: boolean
  localApp?: AppLinkedInterface
  apiKey: string
  newURLs: ApplicationURLs
  developerPlatformClient: DeveloperPlatformClient
}

export async function shouldOrPromptUpdateURLs(options: ShouldOrPromptUpdateURLsOptions): Promise<boolean> {
  if (options.localApp && options.localApp.configuration.client_id !== options.apiKey) return true
  if (options.newApp ?? !terminalSupportsPrompting()) return true
  let shouldUpdateURLs: boolean = options.cachedUpdateURLs === true

  if (options.cachedUpdateURLs === undefined) {
    shouldUpdateURLs = await updateURLsPrompt(
      options.developerPlatformClient.supportsDevSessions,
      options.currentURLs.applicationUrl,
      options.currentURLs.redirectUrlWhitelist,
      options.newURLs,
    )

    if (options.localApp) {
      const localConfiguration = options.localApp.configuration
      localConfiguration.build = {
        ...localConfiguration.build,
        automatically_update_urls_on_dev: shouldUpdateURLs,
      }
      const path = options.localApp.configuration.path
      await setManyAppConfigValues(path, [{keyPath: 'build.automatically_update_urls_on_dev', value: shouldUpdateURLs}])
    } else {
      setCachedAppInfo({directory: options.appDirectory, updateURLs: shouldUpdateURLs})
    }
  }
  return shouldUpdateURLs
}

export function validateApplicationURLs(urls: ApplicationURLs): void {
  if (!isValidURL(urls.applicationUrl))
    throw new AbortError(`Invalid application URL: ${urls.applicationUrl}`, 'Valid format: "https://example.com"')

  urls.redirectUrlWhitelist.forEach((url) => {
    if (!isValidURL(url))
      throw new AbortError(
        `Invalid redirection URLs: ${urls.redirectUrlWhitelist}`,
        'Valid format: "https://example.com/callback1,https://example.com/callback2"',
      )
  })

  if (urls.appProxy?.proxyUrl && !isValidURL(urls.appProxy.proxyUrl)) {
    throw new AbortError(`Invalid app proxy URL: ${urls.appProxy.proxyUrl}`, 'Valid format: "https://example.com"')
  }
}

export async function startTunnelPlugin(config: Config, port: number, provider: string): Promise<TunnelClient> {
  const hooks = await fanoutHooks(config, 'tunnel_start', {port, provider})
  const results = Object.values(hooks).filter(
    (tunnelResponse) => !tunnelResponse?.isErr() || tunnelResponse.error.type !== 'invalid-provider',
  )
  if (results.length > 1) throw new BugError(`Multiple tunnel plugins for ${provider} found`)
  const first = results[0]
  if (!first) throw new BugError(`We couldn't find the ${provider} tunnel plugin`)
  if (first.isErr()) {
    throw new AbortError(`${provider} failed to start the tunnel.\n${first.error.message}`, [
      'What to try:',
      {
        list: {
          items: [
            ['Try to run the command again'],
            ['Add the flag', {command: '--tunnel-url {URL}'}, 'to use a custom tunnel URL'],
          ],
        },
      },
    ])
  }
  return first.value
}
